import { LANG_NAMES, LANGS, type Lang } from '@hive/i18n';
import { useRef, type KeyboardEvent } from 'react';

/**
 * Bandeiras dos idiomas em SVG embutido (o emoji de bandeira não aparece no Windows): Estados Unidos para o inglês,
 * Brasil para o português e Espanha para o espanhol. Desenho simplificado, legível em 20 px.
 */
const FLAGS: Record<Lang, string> = {
  en:
    '<rect width="30" height="20" fill="#B22234"/>' +
    [1, 3, 5, 7, 9, 11].map((i) => `<rect y="${((i * 20) / 13).toFixed(2)}" width="30" height="${(20 / 13).toFixed(2)}" fill="#FFFFFF"/>`).join('') +
    '<rect width="13" height="10.77" fill="#3C3B6E"/>' +
    [2.2, 5.2, 8.2, 11.2]
      .flatMap((x) => [2, 4.7, 7.4].map((y) => `<circle cx="${x}" cy="${y}" r="0.75" fill="#FFFFFF"/>`))
      .join(''),
  'pt-BR':
    '<rect width="30" height="20" fill="#009C3B"/><path d="M15 2.2 27.6 10 15 17.8 2.4 10z" fill="#FFDF00"/>' +
    '<circle cx="15" cy="10" r="4.6" fill="#002776"/><path d="M10.6 9.1c3-1 6.4-.6 8.9 1.3" stroke="#FFFFFF" stroke-width="0.9" fill="none"/>',
  es: '<rect width="30" height="20" fill="#AA151B"/><rect y="5" width="30" height="10" fill="#F1BF00"/>',
};

/** Bandeira de um idioma; decorativa por padrão (quem usa dá o nome no botão). */
export function Flag({ lang, label }: { lang: Lang; label?: string }) {
  return (
    <svg
      className="flag"
      viewBox="0 0 30 20"
      width="24"
      height="16"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      dangerouslySetInnerHTML={{ __html: FLAGS[lang] }}
    />
  );
}

/**
 * Seletor de idioma com as três bandeiras (grupo de opções): Tab entra no idioma atual, setas trocam, Enter ou espaço
 * confirmam o que está em foco. Cada bandeira diz o nome do idioma no próprio idioma (leitor de tela e dica).
 */
export function LangFlags({ value, onChange, label, id }: { value: Lang; onChange: (l: Lang) => void; label: string; id?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const move = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    e.stopPropagation();
    const next = LANGS[(i + step + LANGS.length) % LANGS.length] ?? value;
    ref.current?.querySelector<HTMLElement>(`[data-lang="${next}"]`)?.focus();
    onChange(next);
  };
  return (
    <div ref={ref} id={id} className="lang-flags" role="radiogroup" aria-label={label}>
      {LANGS.map((l, i) => (
        <button
          key={l}
          type="button"
          role="radio"
          data-lang={l}
          lang={l}
          aria-checked={l === value}
          aria-label={LANG_NAMES[l]}
          title={LANG_NAMES[l]}
          tabIndex={l === value ? 0 : -1}
          className="lang-flag"
          onClick={() => l !== value && onChange(l)}
          onKeyDown={(e) => move(e, i)}
        >
          <Flag lang={l} />
        </button>
      ))}
    </div>
  );
}
